import { CommunityTransactionForSaleQuery, CompanyParametersQuery, ContextKeys, CurrencyCollectionQuery, UnitQuery, VatRateForPurchaseQuery, VatRateForSaleBaseQuery, VatRateForSaleWstoQuery, sortVatRates } from '@skylab-shared/business';
import { LocalContext } from '@skylab-shared/core';
import { CalculationMethod, DiscountKind, InventoryDocumentItemModel, InventoryDocumentKind, InventoryGoodsIssueDocumentModel, InventoryGoodsReceiptDocumentModel, ProductModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, head } from '@skylab-shared/utils';
import { ProductRepository } from '../../product/product.repo';

export const InventoryItemQuantityPrecision = 6;

export async function refreshInventoryIssueDocumentItemPriceAsync(
    item: InventoryDocumentItemModel,
    localContext: LocalContext,
    model: InventoryGoodsIssueDocumentModel,
    productRepository: ProductRepository
): Promise<void> {
    if (!item.productId)
        return;

    const { precision } = localContext.firstPreloaded(CurrencyCollectionQuery, { id: model.currencyId });
    const { systemCurrency } = localContext.getPreloaded(CompanyParametersQuery);
    const systemCurrencyId = localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;

    const multiplier = (model.currencyId !== systemCurrencyId) && model.exchangeRate ? (model.exchangeRate.rate || 1) / (model.exchangeRate.units || 1) : 1;
    const product = await localContext.getAsync(ContextKeys.product(item.productId), () => productRepository.findAsync(item.productId!));

    const price = product.prices.find(elem => elem.currencyId === model.currencyId);
    const priceBaseCurrency = product.prices.find(elem => elem.currencyId === systemCurrencyId);

    switch (model.calculationMethod) {
        case CalculationMethod.Net: {
            const newPrice = price?.net ?? (priceBaseCurrency?.net! / multiplier);
            item.price_netBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;

        case CalculationMethod.Gross: {
            const newPrice = price?.gross ?? (priceBaseCurrency?.gross! / multiplier);
            item.price_grossBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;
    }

    if (item.discountKind === DiscountKind.Quota) {
        const newDiscountValue = item.discountValue / multiplier;
        item.discountValue = Calc.round(newDiscountValue, precision, MidpointRounding.AwayFromZero);
    }
}

export async function refreshInventoryReceiptDocumentItemPriceAsync(
    item: InventoryDocumentItemModel,
    localContext: LocalContext,
    model: InventoryGoodsReceiptDocumentModel,
    product: ProductModel
): Promise<void> {
    if (!item.productId)
        return;

    const { precision } = localContext.firstPreloaded(CurrencyCollectionQuery, { id: model.currencyId });
    const { systemCurrency } = localContext.getPreloaded(CompanyParametersQuery);
    const systemCurrencyId = localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;

    const multiplier = (model.currencyId !== systemCurrencyId) && model.exchangeRate ? (model.exchangeRate.rate || 1) / (model.exchangeRate.units || 1) : 1;

    const price = product.purchasePrice || { net: 0, gross: 0 };

    switch (model.calculationMethod) {
        case CalculationMethod.Net: {
            const newPrice = (systemCurrencyId === model.currencyId) ? price.net : (price.net / multiplier);
            item.price_netBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;

        case CalculationMethod.Gross: {
            const newPrice = (systemCurrencyId === model.currencyId) ? price.gross : (price.gross / multiplier);
            item.price_grossBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;
    }

    if (item.discountKind === DiscountKind.Quota) {
        const newDiscountValue = item.discountValue / multiplier;
        item.discountValue = Calc.round(newDiscountValue, precision, MidpointRounding.AwayFromZero);
    }
}

export async function fillInventoryReceiptDocumentProductPropertiesAsync(
    args: { id: number, name: string },
    localContext: LocalContext,
    item: InventoryDocumentItemModel,
    model: InventoryGoodsReceiptDocumentModel,
    product: ProductModel
): Promise<void> {
    item.productId = args.id || null;
    item.discountKind = DiscountKind.Percentage;

    const vatRates = await localContext.refresh(VatRateForPurchaseQuery);
    const productVatRate = vatRates.find(elem => elem.id === product.purchaseVatRateId);
    const exemptVatRateId = vatRates.find(vatRate => vatRate.exempt)?.id;

    item.vatRateId = ((model.kind === InventoryDocumentKind.InternalGoodsReceipt) && !model.issuedByVatPayer
        ? exemptVatRateId
        : productVatRate?.id || (model.issuedByVatPayer ? vatRates.find(vatRate => vatRate.default)?.id : exemptVatRateId))!;

    item.unitId = product.unitId || (localContext.firstPreloaded(UnitQuery, { default: true })).id;
    item.cnCode = product.cnCode;
    item.symbol = product.symbol;
    item.barCode = product.barCode;
    item.jpkVatGroup = head(product.jpkVatGroups) ?? null;
    item.productName = args.name;
    item.pkwiu = product.pkwiu;
    item.inventoryLevel = product.inventoryLevel;
    item.quantity = 1;
    item.price_netBeforeDiscount = 0;
    item.price_grossBeforeDiscount = 0;
}

export async function fillInventoryIssueDocumentProductPropertiesAsync(
    args: { id: number, name: string },
    localContext: LocalContext,
    item: InventoryDocumentItemModel,
    document: InventoryGoodsIssueDocumentModel,
    productRepository: ProductRepository
): Promise<{ vatRateId: {current: number, previous: number, wstoVatRateOnProduct: number | null}}> {
    if (!args.id) {
        throw new Error('ProductId cannot be null on goods issue documents');
    }

    item.productId = args.id;
    item.discountKind = DiscountKind.Percentage;

    const product = args.id ? await localContext.getAsync(ContextKeys.product(args.id), () => productRepository.findAsync(args.id)) : new ProductModel();
    const isWstoOssDocument = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WSTO-OSS' }).id === document.communityTransactionId;
    let wstoVatRateOnProduct = null;

    //#region vat rates
    if (isWstoOssDocument) {
        if (document.wstoConsumerCountryId) {
            const countryVatRates = localContext.filterPreloaded(VatRateForSaleWstoQuery, { countryId: document.wstoConsumerCountryId, active: true });
            const vatRateOnProduct = countryVatRates.find(elem => elem.id === product.ossVatRates.find(elem => elem.countryId === document.wstoConsumerCountryId)?.vatRateId);

            if (vatRateOnProduct?.active) {
                item.vatRateId = wstoVatRateOnProduct = vatRateOnProduct.id;
            } else if (countryVatRates?.length) {
                const defaultVatRateId = countryVatRates.find(x => x.default)?.id;
                item.vatRateId = defaultVatRateId || head(sortVatRates(countryVatRates)).id;
            }
        }
    } else {
        const vatRates = await localContext.refresh(VatRateForSaleBaseQuery);
        if (!item.productId) {
            const vatRate = document.issuedByVatPayer ? vatRates.find(vatRate => vatRate.default) : vatRates.find(vatRate => vatRate.exempt);
            product.vatRateId = vatRate!.id;
        }

        const availableVatRates = vatRates.filter(v => v.communityTransactions.indexOf(document.communityTransactionId) > -1);
        const defaultVatRateId = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { id: document.communityTransactionId })?.defaultVatRateId
            || availableVatRates.find(vatRate => vatRate.default)?.id;

        item.vatRateId = (document.issuedByVatPayer
            ? availableVatRates.some(v => v.id === product.vatRateId) ? product.vatRateId : defaultVatRateId
            : vatRates.find(v => v.exempt)?.id)!;
    }
    //#endregion

    item.unitId = product.unitId || (localContext.firstPreloaded(UnitQuery, { default: true })).id;
    item.cnCode = product.cnCode;
    item.jpkVatGroup = head(product.jpkVatGroups) ?? null;
    item.productName = args.name;
    item.pkwiu = product.pkwiu;
    item.barCode = product.barCode;
    item.symbol = product.symbol;
    item.inventoryLevel = product.inventoryLevel;
    item.quantity = 1;
    item.price_netBeforeDiscount = 0;
    item.price_grossBeforeDiscount = 0;
    item.inventoryUnitCost = item.inventoryTotalCost = document.calculationMethod === CalculationMethod.Net ? product.purchasePrice?.net || 0 : product.purchasePrice?.gross || 0;

    return {
        vatRateId: {
            current: item.vatRateId,
            previous: item.productId && product.vatRateId,
            wstoVatRateOnProduct
        }
    };
}
